import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DsButtonV2Component } from '../buttons/ds-button-v2.component';
import { DsIconButtonV2Component } from '../buttons/ds-icon-button-v2.component';
import { DsIconComponent } from '../icons/ds-icon.component';

export type DsDateInputState = 'default' | 'error';
export type DsDatePickerPresentation = 'popover' | 'modal';
export type DsDatePickerView = 'day' | 'month' | 'year';

export interface DsCalendarDate {
  year: number;
  month: number;
  day: number;
}

interface DsDateCell {
  date: DsCalendarDate;
  iso: string;
  day: number;
  label: string;
  outsideMonth: boolean;
  disabled: boolean;
  selected: boolean;
  today: boolean;
}

interface DsMonthCell {
  value: number;
  label: string;
  disabled: boolean;
  selected: boolean;
}

interface DsYearCell {
  value: number;
  disabled: boolean;
  selected: boolean;
}

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
] as const;

const WEEKDAYS = [
  { short: 'L', full: 'Lunes' },
  { short: 'M', full: 'Martes' },
  { short: 'X', full: 'Miércoles' },
  { short: 'J', full: 'Jueves' },
  { short: 'V', full: 'Viernes' },
  { short: 'S', full: 'Sábado' },
  { short: 'D', full: 'Domingo' },
] as const;

@Component({
  selector: 'ds-input-date',
  standalone: true,
  imports: [DsButtonV2Component, DsIconButtonV2Component, DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="ds-date"
      [class.ds-date--error]="hasError"
      [class.ds-date--disabled]="disabled"
      [attr.aria-disabled]="disabled || null"
    >
      @if (label) {
        <label class="ds-date__label" [attr.for]="inputId">{{ label }}</label>
      }

      <div class="ds-date__anchor">
        <div #field class="ds-date__field" (click)="focusInput()">
          <input
            #inputControl
            class="ds-date__input"
            [attr.id]="inputId"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            maxlength="10"
            [placeholder]="placeholder"
            [value]="displayValue"
            [disabled]="disabled"
            [attr.aria-invalid]="hasError ? 'true' : null"
            [attr.aria-describedby]="showHelpText && helpText ? helpId : null"
            [attr.aria-controls]="expanded ? panelId : null"
            (input)="onInput($event)"
            (focus)="focused.emit($event)"
            (blur)="onBlur($event)"
          />

          @if (hasError) {
            <span class="ds-date__error-icon" aria-hidden="true">
              <ds-icon name="ErrorFill" size="var(--ds-input-trailing-icon-size)" />
            </span>
          }
          <ds-icon-button-v2
            #calendarButton
            class="ds-date__calendar-button"
            icon="CalendarMonth"
            size="xs"
            emphasis="terciary"
            [disabled]="disabled"
            [ariaLabel]="calendarButtonLabel"
            ariaHasPopup="dialog"
            [ariaExpanded]="expanded"
            [ariaControls]="expanded ? panelId : ''"
            (clicked)="togglePicker()"
          />
        </div>

        @if (expanded && presentation === 'modal') {
          <div class="ds-date__backdrop" (click)="cancelSelection()"></div>
        }

        @if (expanded) {
          <section
            #panel
            class="ds-date__panel"
            [class.ds-date__panel--modal]="presentation === 'modal'"
            [attr.id]="panelId"
            role="dialog"
            aria-modal="true"
            [attr.aria-label]="dialogLabel"
            [style.left.px]="presentation === 'popover' ? panelLeft : null"
            [style.top.px]="presentation === 'popover' ? panelTop : null"
            (keydown)="onPanelKeydown($event)"
          >
            <div class="ds-date__header" aria-live="polite">
              <ds-icon-button-v2
                icon="KeyboardArrowLeft"
                size="s"
                emphasis="terciary"
                [disabled]="!canNavigatePrevious"
                [ariaLabel]="previousLabel"
                (clicked)="navigatePrevious()"
              />

              <div class="ds-date__header-title">
                @if (view === 'day') {
                  <button
                    type="button"
                    class="ds-date__header-button"
                    aria-label="Elegir mes"
                    (click)="showMonthView()"
                  >{{ monthName }}</button>
                  <button
                    type="button"
                    class="ds-date__header-button"
                    aria-label="Elegir año"
                    (click)="showYearView()"
                  >{{ displayedDate.year }}</button>
                } @else if (view === 'month') {
                  <button
                    type="button"
                    class="ds-date__header-button"
                    aria-label="Elegir año"
                    (click)="showYearView()"
                  >{{ displayedDate.year }}</button>
                } @else {
                  <span class="ds-date__year-range">{{ yearPageStart }} – {{ yearPageEnd }}</span>
                }
              </div>

              <ds-icon-button-v2
                icon="KeyboardArrowRight"
                size="s"
                emphasis="terciary"
                [disabled]="!canNavigateNext"
                [ariaLabel]="nextLabel"
                (clicked)="navigateNext()"
              />
            </div>

            @if (view === 'day') {
              <div class="ds-date__calendar" role="grid" [attr.aria-label]="monthName + ' ' + displayedDate.year">
                <div class="ds-date__weekdays" role="row">
                  @for (weekday of weekdays; track weekday.short) {
                    <div class="ds-date__weekday" role="columnheader">
                      <abbr [attr.title]="weekday.full">{{ weekday.short }}</abbr>
                    </div>
                  }
                </div>

                <div class="ds-date__days">
                  @for (cell of dayCells; track cell.iso) {
                    <div class="ds-date__grid-cell" role="gridcell" [attr.aria-selected]="cell.selected">
                      <button
                        type="button"
                        class="ds-date__day"
                        [class.ds-date__day--outside]="cell.outsideMonth"
                        [class.ds-date__day--selected]="cell.selected"
                        [class.ds-date__day--today]="cell.today"
                        [disabled]="cell.disabled"
                        [attr.data-date]="cell.iso"
                        [attr.tabindex]="cell.iso === focusedDateIso ? 0 : -1"
                        [attr.aria-label]="cell.label"
                        [attr.aria-current]="cell.today ? 'date' : null"
                        (click)="selectDate(cell.date)"
                        (focus)="focusedDateIso = cell.iso"
                        (keydown)="onDayKeydown($event, cell.date)"
                      >{{ cell.day }}</button>
                    </div>
                  }
                </div>
              </div>
            } @else if (view === 'month') {
              <div class="ds-date__choice-grid" role="grid" [attr.aria-label]="'Mes de ' + displayedDate.year">
                @for (month of monthCells; track month.value) {
                  <button
                    type="button"
                    class="ds-date__choice"
                    [class.ds-date__choice--selected]="month.selected"
                    [disabled]="month.disabled"
                    [attr.aria-selected]="month.selected"
                    (click)="selectMonth(month.value)"
                  >{{ month.label }}</button>
                }
              </div>
            } @else {
              <div class="ds-date__choice-grid" role="grid" aria-label="Año">
                @for (year of yearCells; track year.value) {
                  <button
                    type="button"
                    class="ds-date__choice"
                    [class.ds-date__choice--selected]="year.selected"
                    [disabled]="year.disabled"
                    [attr.aria-selected]="year.selected"
                    (click)="selectYear(year.value)"
                  >{{ year.value }}</button>
                }
              </div>
            }

            <div class="ds-date__footer">
              <ds-button-v2
                label="Cancelar"
                size="xs"
                emphasis="terciary"
                (clicked)="cancelSelection()"
              />
              <ds-button-v2
                label="Aplicar"
                size="xs"
                emphasis="primary"
                [disabled]="!draftDate"
                (clicked)="applySelection()"
              />
            </div>
          </section>
        }
      </div>

      @if (showHelpText && helpText) {
        <span
          class="ds-date__help"
          [attr.id]="helpId"
          [attr.role]="hasError ? 'alert' : null"
        >{{ helpText }}</span>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }

    .ds-date {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: var(--ds-input-gap);
      min-width: var(--ds-date-picker-input-min-width);
    }

    .ds-date--disabled {
      opacity: calc(var(--ds-date-picker-disabled-opacity) / 100px);
      pointer-events: none;
    }

    .ds-date__label,
    .ds-date__help,
    .ds-date__input,
    .ds-date__panel button,
    .ds-date__year-range {
      font-family: 'Sura Sans', system-ui, sans-serif;
    }

    .ds-date__label {
      font-weight: 400;
      font-size: var(--ds-input-label-font-size);
      line-height: var(--ds-input-label-line-height);
      color: var(--ds-input-label-color);
    }

    .ds-date__anchor { position: relative; }

    .ds-date__field {
      display: flex;
      align-items: center;
      gap: var(--ds-input-icon-gap);
      height: var(--ds-input-field-height);
      padding: var(--ds-input-padding-y) var(--ds-input-padding-x);
      box-sizing: border-box;
      border: var(--ds-input-border-width) solid var(--ds-input-border-color);
      border-radius: var(--ds-input-border-radius);
      background: var(--ds-input-background);
      cursor: text;
      transition: border-color var(--ds-date-picker-transition-duration) ease-out;
    }

    .ds-date--error .ds-date__field {
      border-color: var(--ds-input-border-color-error);
      background: var(--ds-input-background-error);
    }

    .ds-date__field:focus-within {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-date__input {
      flex: 1 1 0;
      min-width: 0;
      width: 100%;
      height: 100%;
      padding: 0;
      border: 0;
      outline: 0;
      background: transparent;
      color: var(--ds-input-value-color);
      caret-color: var(--ds-input-cursor-color);
      font-weight: 400;
      font-size: var(--ds-input-field-font-size);
      line-height: var(--ds-input-field-line-height);
    }

    .ds-date__input::placeholder { color: var(--ds-input-placeholder-color); }

    .ds-date__calendar-button,
    .ds-date__error-icon {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      color: var(--ds-input-arrow-color);
    }

    .ds-date__error-icon { color: var(--ds-input-error-icon-color); }

    .ds-date__help {
      color: var(--ds-input-help-color);
      font-weight: 400;
      font-size: var(--ds-input-help-font-size);
      line-height: var(--ds-input-help-line-height);
    }

    .ds-date--error .ds-date__help { color: var(--ds-input-help-color-error); }

    .ds-date__backdrop {
      position: fixed;
      inset: 0;
      z-index: var(--ds-date-picker-z-index);
      background: var(--ds-date-picker-overlay);
    }

    .ds-date__panel {
      position: fixed;
      z-index: var(--ds-date-picker-z-index);
      display: flex;
      flex-direction: column;
      gap: var(--ds-date-picker-panel-gap);
      width: var(--ds-date-picker-panel-width);
      max-width: calc(100vw - (var(--ds-date-picker-viewport-margin) * 2));
      padding: var(--ds-date-picker-panel-padding);
      box-sizing: border-box;
      border: var(--ds-date-picker-panel-border-width) solid var(--ds-date-picker-panel-border-color);
      border-radius: var(--ds-date-picker-panel-border-radius);
      background: var(--ds-date-picker-panel-background);
      box-shadow: var(--ds-date-picker-panel-shadow);
      color: var(--ds-date-picker-content-color);
    }

    .ds-date__panel--modal {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: calc(var(--ds-date-picker-z-index) + 1);
    }

    .ds-date__header {
      display: flex;
      align-items: center;
      gap: var(--ds-date-picker-section-gap);
    }

    .ds-date__header-title {
      display: flex;
      flex: 1 1 0;
      align-items: center;
      justify-content: center;
    }

    .ds-date__header-button {
      padding: 0 var(--ds-date-picker-section-gap);
      border: 0;
      border-radius: var(--ds-date-picker-item-border-radius);
      background: transparent;
      color: var(--ds-date-picker-content-color);
      cursor: pointer;
      font-size: var(--ds-date-picker-header-font-size);
      line-height: var(--ds-date-picker-header-line-height);
      font-weight: var(--ds-date-picker-header-font-weight);
    }

    .ds-date__header-button:hover { background: var(--ds-date-picker-hover-background); }

    .ds-date__header-button:focus-visible,
    .ds-date__day:focus-visible,
    .ds-date__choice:focus-visible {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-date__year-range {
      color: var(--ds-date-picker-content-color);
      font-size: var(--ds-date-picker-header-font-size);
      line-height: var(--ds-date-picker-header-line-height);
      font-weight: var(--ds-date-picker-header-font-weight);
    }

    .ds-date__calendar {
      display: flex;
      flex-direction: column;
      gap: var(--ds-date-picker-section-gap);
    }

    .ds-date__weekdays,
    .ds-date__days {
      display: grid;
      grid-template-columns: repeat(7, var(--ds-date-picker-day-size));
      gap: var(--ds-date-picker-grid-gap);
    }

    .ds-date__weekday {
      display: flex;
      align-items: center;
      justify-content: center;
      height: var(--ds-date-picker-weekday-height);
      color: var(--ds-date-picker-muted-content-color);
      font-size: var(--ds-date-picker-item-font-size);
      line-height: var(--ds-date-picker-item-line-height);
      font-weight: var(--ds-date-picker-selected-item-font-weight);
    }

    .ds-date__weekday abbr { text-decoration: none; }
    .ds-date__grid-cell { width: var(--ds-date-picker-day-size); height: var(--ds-date-picker-day-size); }

    .ds-date__day,
    .ds-date__choice {
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      border: var(--ds-date-picker-item-border-width) solid transparent;
      border-radius: var(--ds-date-picker-item-border-radius);
      background: transparent;
      color: var(--ds-date-picker-content-color);
      cursor: pointer;
      font-size: var(--ds-date-picker-item-font-size);
      line-height: var(--ds-date-picker-item-line-height);
      font-weight: var(--ds-date-picker-item-font-weight);
      transition:
        background var(--ds-date-picker-transition-duration) ease-out,
        color var(--ds-date-picker-transition-duration) ease-out;
    }

    .ds-date__day {
      width: var(--ds-date-picker-day-size);
      height: var(--ds-date-picker-day-size);
      padding: 0;
    }

    .ds-date__day:hover:not(:disabled),
    .ds-date__choice:hover:not(:disabled) { background: var(--ds-date-picker-hover-background); }

    .ds-date__day--outside { color: var(--ds-date-picker-outside-month-content-color); }

    .ds-date__day--today:not(.ds-date__day--selected) {
      border-color: var(--ds-date-picker-today-border-color);
    }

    .ds-date__day--selected,
    .ds-date__choice--selected {
      background: var(--ds-date-picker-selected-background);
      color: var(--ds-date-picker-selected-content-color);
      font-weight: var(--ds-date-picker-selected-item-font-weight);
    }

    .ds-date__day:disabled,
    .ds-date__choice:disabled {
      opacity: calc(var(--ds-date-picker-disabled-opacity) / 100px);
      cursor: not-allowed;
    }

    .ds-date__choice-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: var(--ds-date-picker-grid-gap);
    }

    .ds-date__choice {
      height: var(--ds-date-picker-month-year-item-height);
      padding: 0 var(--ds-date-picker-section-gap);
    }

    .ds-date__footer {
      display: flex;
      justify-content: flex-end;
      gap: var(--ds-date-picker-section-gap);
    }
  `],
})
export class DsInputDateComponent implements OnChanges {
  private static counter = 0;

  @ViewChild('inputControl') private inputControl?: ElementRef<HTMLInputElement>;
  @ViewChild('field') private field?: ElementRef<HTMLElement>;
  @ViewChild('calendarButton', { read: ElementRef }) private calendarButton?: ElementRef<HTMLElement>;
  @ViewChild('panel') private panel?: ElementRef<HTMLElement>;

  readonly inputId = `ds-input-date-${++DsInputDateComponent.counter}`;
  readonly helpId = `${this.inputId}-help`;
  readonly panelId = `${this.inputId}-panel`;
  readonly weekdays = WEEKDAYS;

  @Input() value = '';
  @Input() label = '';
  @Input() placeholder = 'DD/MM/AAAA';
  @Input() helpText = '';
  @Input() showHelpText = false;
  @Input() state: DsDateInputState = 'default';
  @Input() disabled = false;
  @Input() min = '';
  @Input() max = '';
  @Input() yearStart = 1900;
  @Input() yearEnd = 2100;
  @Input() presentation: DsDatePickerPresentation = 'popover';
  @Input() today = '';
  @Input() calendarButtonLabel = 'Abrir selector de fecha';
  @Input() dialogLabel = 'Seleccionar fecha';

  @Output() valueChange = new EventEmitter<string>();
  @Output() validityChange = new EventEmitter<boolean>();
  @Output() expandedChange = new EventEmitter<boolean>();
  @Output() focused = new EventEmitter<FocusEvent>();
  @Output() blurred = new EventEmitter<FocusEvent>();

  displayValue = '';
  expanded = false;
  view: DsDatePickerView = 'day';
  displayedDate: DsCalendarDate = this.systemToday();
  draftDate: DsCalendarDate | null = null;
  focusedDateIso = '';
  panelLeft = 0;
  panelTop = 0;
  private manualError = false;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      const parsed = parseIsoDate(this.value);
      this.displayValue = parsed ? formatDisplayDate(parsed) : '';
    }
    this.yearStart = Math.max(1, Math.trunc(this.yearStart));
    this.yearEnd = Math.trunc(this.yearEnd);
    if (this.yearEnd < this.yearStart) {
      this.yearEnd = this.yearStart;
    }
  }

  get hasError(): boolean {
    return this.state === 'error' || this.manualError;
  }

  get monthName(): string {
    return MONTH_NAMES[this.displayedDate.month - 1];
  }

  get previousLabel(): string {
    return this.view === 'day' ? 'Mes anterior' : this.view === 'month' ? 'Año anterior' : 'Años anteriores';
  }

  get nextLabel(): string {
    return this.view === 'day' ? 'Mes siguiente' : this.view === 'month' ? 'Año siguiente' : 'Años siguientes';
  }

  get yearPageStart(): number {
    const pageSize = 12;
    return Math.min(
      this.yearEnd,
      this.yearStart + Math.floor((Math.max(this.yearStart, this.displayedDate.year) - this.yearStart) / pageSize) * pageSize,
    );
  }

  get yearPageEnd(): number {
    return Math.min(this.yearEnd, this.yearPageStart + 11);
  }

  get dayCells(): DsDateCell[] {
    const first = { year: this.displayedDate.year, month: this.displayedDate.month, day: 1 };
    const start = addDays(first, -dayOfWeekMondayBased(first));
    const selectedIso = this.draftDate ? formatIsoDate(this.draftDate) : '';
    const todayIso = formatIsoDate(this.resolvedToday());
    return Array.from({ length: 42 }, (_, index) => {
      const date = addDays(start, index);
      const iso = formatIsoDate(date);
      return {
        date,
        iso,
        day: date.day,
        label: `${date.day} de ${MONTH_NAMES[date.month - 1]} de ${date.year}`,
        outsideMonth: date.month !== this.displayedDate.month,
        disabled: !this.isDateEnabled(date),
        selected: iso === selectedIso,
        today: iso === todayIso,
      };
    });
  }

  get monthCells(): DsMonthCell[] {
    return MONTH_NAMES.map((name, index) => {
      const month = index + 1;
      return {
        value: month,
        label: name.slice(0, 3),
        disabled: !this.monthHasEnabledDate(this.displayedDate.year, month),
        selected: month === this.displayedDate.month,
      };
    });
  }

  get yearCells(): DsYearCell[] {
    return Array.from({ length: this.yearPageEnd - this.yearPageStart + 1 }, (_, index) => {
      const year = this.yearPageStart + index;
      return {
        value: year,
        disabled: !this.yearHasEnabledDate(year),
        selected: year === this.displayedDate.year,
      };
    });
  }

  get canNavigatePrevious(): boolean {
    if (this.view === 'day') {
      return this.monthHasEnabledDate(addMonths(this.displayedDate, -1).year, addMonths(this.displayedDate, -1).month);
    }
    if (this.view === 'month') return this.displayedDate.year > this.yearStart;
    return this.yearPageStart > this.yearStart;
  }

  get canNavigateNext(): boolean {
    if (this.view === 'day') {
      return this.monthHasEnabledDate(addMonths(this.displayedDate, 1).year, addMonths(this.displayedDate, 1).month);
    }
    if (this.view === 'month') return this.displayedDate.year < this.yearEnd;
    return this.yearPageEnd < this.yearEnd;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.displayValue = maskDateInput(input.value);
    input.value = this.displayValue;

    if (!this.displayValue) {
      this.value = '';
      this.manualError = false;
      this.valueChange.emit('');
      this.validityChange.emit(true);
      return;
    }

    const parsed = parseDisplayDate(this.displayValue);
    const valid = !!parsed && this.isDateEnabled(parsed);
    this.manualError = this.displayValue.length === 10 && !valid;
    this.validityChange.emit(valid || this.displayValue.length < 10);
    if (valid && parsed) {
      this.value = formatIsoDate(parsed);
      this.valueChange.emit(this.value);
    }
  }

  onBlur(event: FocusEvent): void {
    if (this.displayValue && this.displayValue.length < 10) {
      this.manualError = true;
      this.validityChange.emit(false);
    }
    this.blurred.emit(event);
  }

  focusInput(): void {
    if (!this.disabled) this.inputControl?.nativeElement.focus();
  }

  togglePicker(): void {
    if (this.expanded) {
      this.cancelSelection();
    } else {
      this.openPicker();
    }
  }

  openPicker(): void {
    if (this.disabled) return;
    const initial = this.clampDate(parseIsoDate(this.value) ?? this.resolvedToday());
    this.draftDate = parseIsoDate(this.value);
    this.displayedDate = { ...initial, day: 1 };
    this.focusedDateIso = formatIsoDate(this.draftDate ?? initial);
    this.view = 'day';
    this.setExpanded(true);
    this.positionPopover();
    this.focusCurrentDay();
  }

  cancelSelection(): void {
    this.setExpanded(false);
    this.restoreTriggerFocus();
  }

  applySelection(): void {
    if (!this.draftDate) return;
    this.value = formatIsoDate(this.draftDate);
    this.displayValue = formatDisplayDate(this.draftDate);
    this.manualError = false;
    this.valueChange.emit(this.value);
    this.validityChange.emit(true);
    this.setExpanded(false);
    this.restoreTriggerFocus();
  }

  selectDate(date: DsCalendarDate): void {
    if (!this.isDateEnabled(date)) return;
    this.draftDate = date;
    this.displayedDate = { ...date, day: 1 };
    this.focusedDateIso = formatIsoDate(date);
    this.changeDetector.markForCheck();
  }

  showMonthView(): void {
    this.view = 'month';
    this.changeDetector.markForCheck();
  }

  showYearView(): void {
    this.view = 'year';
    this.changeDetector.markForCheck();
  }

  selectMonth(month: number): void {
    if (!this.monthHasEnabledDate(this.displayedDate.year, month)) return;
    this.displayedDate = { year: this.displayedDate.year, month, day: 1 };
    this.view = 'day';
    this.focusedDateIso = formatIsoDate(this.firstEnabledDateInMonth(this.displayedDate.year, month));
    this.changeDetector.markForCheck();
    this.focusCurrentDay();
  }

  selectYear(year: number): void {
    if (!this.yearHasEnabledDate(year)) return;
    this.displayedDate = { year, month: this.displayedDate.month, day: 1 };
    this.view = 'month';
    this.changeDetector.markForCheck();
  }

  navigatePrevious(): void {
    this.navigate(-1);
  }

  navigateNext(): void {
    this.navigate(1);
  }

  onDayKeydown(event: KeyboardEvent, date: DsCalendarDate): void {
    let target: DsCalendarDate | null = null;
    switch (event.key) {
      case 'ArrowLeft': target = addDays(date, -1); break;
      case 'ArrowRight': target = addDays(date, 1); break;
      case 'ArrowUp': target = addDays(date, -7); break;
      case 'ArrowDown': target = addDays(date, 7); break;
      case 'Home': target = addDays(date, -dayOfWeekMondayBased(date)); break;
      case 'End': target = addDays(date, 6 - dayOfWeekMondayBased(date)); break;
      case 'PageUp': target = addMonths(date, event.shiftKey ? -12 : -1); break;
      case 'PageDown': target = addMonths(date, event.shiftKey ? 12 : 1); break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectDate(date);
        return;
      case 'Escape':
        event.preventDefault();
        this.cancelSelection();
        return;
      default:
        return;
    }

    event.preventDefault();
    const bounded = this.clampDate(target);
    this.displayedDate = { ...bounded, day: 1 };
    this.focusedDateIso = formatIsoDate(bounded);
    this.focusCurrentDay();
  }

  onPanelKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelSelection();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = Array.from<HTMLElement>(
      this.panel?.nativeElement.querySelectorAll('button:not([disabled]), [tabindex="0"]') ?? [],
    ).filter(element => element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  @HostListener('document:pointerdown', ['$event'])
  onDocumentPointerDown(event: PointerEvent): void {
    if (this.expanded && !this.host.nativeElement.contains(event.target as Node)) {
      this.setExpanded(false);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.positionPopover();
  }

  private navigate(direction: -1 | 1): void {
    if (direction < 0 && !this.canNavigatePrevious) return;
    if (direction > 0 && !this.canNavigateNext) return;

    if (this.view === 'day') {
      this.displayedDate = { ...addMonths(this.displayedDate, direction), day: 1 };
    } else if (this.view === 'month') {
      this.displayedDate = { ...this.displayedDate, year: this.displayedDate.year + direction, day: 1 };
    } else {
      const nextYear = Math.max(this.yearStart, Math.min(this.yearEnd, this.displayedDate.year + direction * 12));
      this.displayedDate = { ...this.displayedDate, year: nextYear, day: 1 };
    }
    this.changeDetector.markForCheck();
  }

  private setExpanded(expanded: boolean): void {
    if (this.expanded === expanded) return;
    this.expanded = expanded;
    this.expandedChange.emit(expanded);
    this.changeDetector.markForCheck();
  }

  private resolvedToday(): DsCalendarDate {
    return parseIsoDate(this.today) ?? this.systemToday();
  }

  private systemToday(): DsCalendarDate {
    const date = new Date();
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
  }

  private isDateEnabled(date: DsCalendarDate): boolean {
    if (date.year < this.yearStart || date.year > this.yearEnd) return false;
    const min = parseIsoDate(this.min);
    const max = parseIsoDate(this.max);
    return (!min || compareDates(date, min) >= 0) && (!max || compareDates(date, max) <= 0);
  }

  private monthHasEnabledDate(year: number, month: number): boolean {
    if (year < this.yearStart || year > this.yearEnd) return false;
    const first = { year, month, day: 1 };
    const last = { year, month, day: daysInMonth(year, month) };
    const min = parseIsoDate(this.min);
    const max = parseIsoDate(this.max);
    return (!max || compareDates(first, max) <= 0) && (!min || compareDates(last, min) >= 0);
  }

  private yearHasEnabledDate(year: number): boolean {
    if (year < this.yearStart || year > this.yearEnd) return false;
    const first = { year, month: 1, day: 1 };
    const last = { year, month: 12, day: 31 };
    const min = parseIsoDate(this.min);
    const max = parseIsoDate(this.max);
    return (!max || compareDates(first, max) <= 0) && (!min || compareDates(last, min) >= 0);
  }

  private firstEnabledDateInMonth(year: number, month: number): DsCalendarDate {
    const first = { year, month, day: 1 };
    const min = parseIsoDate(this.min);
    if (min && min.year === year && min.month === month) return min;
    return first;
  }

  private clampDate(date: DsCalendarDate): DsCalendarDate {
    const rangeStart = { year: this.yearStart, month: 1, day: 1 };
    const rangeEnd = { year: this.yearEnd, month: 12, day: 31 };
    const min = parseIsoDate(this.min);
    const max = parseIsoDate(this.max);
    const lower = min && compareDates(min, rangeStart) > 0 ? min : rangeStart;
    const upper = max && compareDates(max, rangeEnd) < 0 ? max : rangeEnd;
    if (compareDates(date, lower) < 0) return lower;
    if (compareDates(date, upper) > 0) return upper;
    return date;
  }

  private focusCurrentDay(): void {
    setTimeout(() => {
      const selector = `[data-date="${this.focusedDateIso}"]`;
      (this.panel?.nativeElement.querySelector(selector) as HTMLElement | null)?.focus();
    }, 0);
  }

  private positionPopover(): void {
    if (!this.expanded || this.presentation !== 'popover') return;
    setTimeout(() => {
      const field = this.field?.nativeElement;
      const panel = this.panel?.nativeElement;
      if (!field || !panel) return;

      const tokens = getComputedStyle(this.host.nativeElement);
      const margin = Number.parseFloat(tokens.getPropertyValue('--ds-date-picker-viewport-margin')) || 0;
      const gap = Number.parseFloat(tokens.getPropertyValue('--ds-date-picker-anchor-gap')) || 0;
      const fieldRect = field.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const maxLeft = Math.max(margin, window.innerWidth - panelRect.width - margin);
      this.panelLeft = Math.max(margin, Math.min(fieldRect.left, maxLeft));
      const below = fieldRect.bottom + gap;
      const above = fieldRect.top - panelRect.height - gap;
      this.panelTop = below + panelRect.height <= window.innerHeight - margin
        ? below
        : Math.max(margin, above);
      this.changeDetector.markForCheck();
    }, 0);
  }

  private restoreTriggerFocus(): void {
    setTimeout(() => {
      this.calendarButton?.nativeElement.querySelector('button')?.focus();
    }, 0);
  }
}

export function parseIsoDate(value: string): DsCalendarDate | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  return createDate(Number(match[1]), Number(match[2]), Number(match[3]));
}

export function parseDisplayDate(value: string): DsCalendarDate | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!match) return null;
  return createDate(Number(match[3]), Number(match[2]), Number(match[1]));
}

export function formatIsoDate(date: DsCalendarDate): string {
  return `${date.year.toString().padStart(4, '0')}-${twoDigits(date.month)}-${twoDigits(date.day)}`;
}

export function formatDisplayDate(date: DsCalendarDate): string {
  return `${twoDigits(date.day)}/${twoDigits(date.month)}/${date.year.toString().padStart(4, '0')}`;
}

function createDate(year: number, month: number, day: number): DsCalendarDate | null {
  if (!Number.isInteger(year) || year < 1 || month < 1 || month > 12) return null;
  if (!Number.isInteger(day) || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

function maskDateInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function addMonths(date: DsCalendarDate, amount: number): DsCalendarDate {
  const total = date.year * 12 + date.month - 1 + amount;
  const year = Math.floor(total / 12);
  const month = ((total % 12) + 12) % 12 + 1;
  return { year, month, day: Math.min(date.day, daysInMonth(year, month)) };
}

function addDays(date: DsCalendarDate, amount: number): DsCalendarDate {
  let result = { ...date };
  const direction = amount < 0 ? -1 : 1;
  for (let index = 0; index < Math.abs(amount); index += 1) {
    if (direction > 0) {
      result = result.day === daysInMonth(result.year, result.month)
        ? { ...addMonths({ ...result, day: 1 }, 1), day: 1 }
        : { ...result, day: result.day + 1 };
    } else {
      if (result.day === 1) {
        const previous = addMonths(result, -1);
        result = { ...previous, day: daysInMonth(previous.year, previous.month) };
      } else {
        result = { ...result, day: result.day - 1 };
      }
    }
  }
  return result;
}

function compareDates(left: DsCalendarDate, right: DsCalendarDate): number {
  return left.year - right.year || left.month - right.month || left.day - right.day;
}

function dayOfWeekMondayBased(date: DsCalendarDate): number {
  const offsets = [0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4];
  const year = date.month < 3 ? date.year - 1 : date.year;
  const sundayBased = (year + Math.floor(year / 4) - Math.floor(year / 100)
    + Math.floor(year / 400) + offsets[date.month - 1] + date.day) % 7;
  return (sundayBased + 6) % 7;
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function twoDigits(value: number): string {
  return value.toString().padStart(2, '0');
}
